import React, { useState, useRef, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';

export default function App() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [keepLeadingBlanks, setKeepLeadingBlanks] = useState(false);
  const [sortResults, setSortResults] = useState(false);
  const [preserveFirst, setPreserveFirst] = useState(true);
  const [history, setHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dedupe_history") || "[]");
    } catch {
      return [];
    }
  });
  const [stats, setStats] = useState({ original: 0, unique: 0, removed: 0 });
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("dedupe_history", JSON.stringify(history));
  }, [history]);

  const parseLines = (text) => text.replace(/\r\n/g, "\n").split("\n");

  const normalizeLine = (line) => {
    const preserved = keepLeadingBlanks ? line : line.trimStart();
    return ignoreCase ? preserved.toLowerCase() : preserved;
  };

  const dedupeText = (text) => {
    const lines = parseLines(text);
    const seen = new Map();
    lines.forEach((line) => {
      const key = normalizeLine(line);
      if (preserveFirst) {
        if (!seen.has(key)) seen.set(key, line);
      } else {
        seen.set(key, line);
      }
    });

    let outputLines = Array.from(seen.values());
    if (sortResults) outputLines.sort((a, b) => a.localeCompare(b));

    setStats({
      original: lines.length,
      unique: outputLines.length,
      removed: lines.length - outputLines.length,
    });
    return outputLines.join("\n");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const out = dedupeText(input);
    setResult(out);
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setInput(String(e.target.result));
    reader.readAsText(file);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "deduped-list.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => navigator.clipboard.writeText(result);
  const handleClear = () => {
    setInput("");
    setResult("");
    setStats({ original: 0, unique: 0, removed: 0 });
  };

  const generateDummyText = () => {
    const dummy = [
      "  Apple",
      "Banana",
      "apple",
      "Mango",
      "  banana",
      "Grapes",
      "Orange",
      "Mango",
      "Cherry",
      "apple",
      "Pineapple",
      "grapes",
    ].join("\n");

    setInput(dummy);
    setResult("");
    setStats({ original: 0, unique: 0, removed: 0 });
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark shadow-sm">
        <div className="container">
          <a className="navbar-brand fw-bold" href="#">DeDupeList+</a>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item"><a className="nav-link active" href="#features">Features</a></li>
              <li className="nav-item"><a className="nav-link" href="#about">About</a></li>
              <li className="nav-item"><a className="nav-link" href="#contact">Contact</a></li>
            </ul>
          </div>
        </div>
      </nav>

      <main className="container my-4 flex-grow-1">
        <div className="text-center mb-4">
          <h2 className="fw-bold">DeDupeList+ – Enhanced Version</h2>
          <p className="text-muted">Remove duplicate lines easily, with options to sort, ignore case, and more.</p>
          <p>DeDupeList+ is an open-source React project built to enhance the classic DeDupeList tool with modern features and UI polish.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <label className="form-label fw-bold">Input</label>
                  <textarea
                    ref={textareaRef}
                    className="form-control"
                    rows={10}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Paste lines here or generate dummy text..."
                  />
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <input type="file" accept=".txt" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => handleFileUpload(e.target.files[0])} />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => fileInputRef.current.click()}>Upload File</button>
                    <button type="submit" className="btn btn-primary">Dedupe</button>
                    <button type="button" className="btn btn-danger" onClick={handleClear}>Clear</button>
                    <button type="button" className="btn btn-warning" onClick={generateDummyText}>Generate Dummy Text</button>
                  </div>

                  <div className="form-check mt-3">
                    <input className="form-check-input" type="checkbox" checked={ignoreCase} onChange={(e) => setIgnoreCase(e.target.checked)} id="ignoreCase" />
                    <label htmlFor="ignoreCase" className="form-check-label">Ignore Case</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={keepLeadingBlanks} onChange={(e) => setKeepLeadingBlanks(e.target.checked)} id="keepBlanks" />
                    <label htmlFor="keepBlanks" className="form-check-label">Keep Leading Blanks</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={sortResults} onChange={(e) => setSortResults(e.target.checked)} id="sortResults" />
                    <label htmlFor="sortResults" className="form-check-label">Sort Results (Alphabetical Order)</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={preserveFirst} onChange={(e) => setPreserveFirst(e.target.checked)} id="preserveFirst" />
                    <label htmlFor="preserveFirst" className="form-check-label">Preserve First Occurrence</label>
                  </div>

                  <div className="text-muted small mt-2">
                    Original: {stats.original} | Unique: {stats.unique} | Removed: {stats.removed}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <label className="form-label fw-bold">Result</label>
                  <textarea className="form-control" rows={10} value={result} readOnly />
                  <div className="mt-3 d-flex gap-2">
                    <button type="button" className="btn btn-outline-success" onClick={handleDownload} disabled={!result}>Download</button>
                    <button type="button" className="btn btn-outline-secondary" onClick={handleCopy} disabled={!result}>Copy</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-dark text-light mt-auto py-5">
        <div className="container">
          <div className="row align-items-center mb-4" id="about">
            <div className="col-md-3 text-center mb-3 mb-md-0">
              <div className="rounded-circle overflow-hidden mx-auto" style={{ width: "150px", height: "150px", backgroundColor: "#555" }}>
                <img src="/placeholder-profile.png" alt="Pavithran M" className="img-fluid h-100 w-100 object-fit-cover" />
              </div>
            </div>
            <div className="col-md-9">
              <h4 className="fw-bold">About Me</h4>
              <p className="mb-0">Pavithran M, a pre-final year student from CEG, Anna University. A budding engineer who is willing to do creative projects and combine technology with imagination to build meaningful tools that simplify life.</p>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3" id="features">
              <h5>Features</h5>
              <ul className="list-unstyled">
                <li>✔ Remove duplicates instantly</li>
                <li>✔ Sort and format text easily</li>
                <li>✔ Upload / Download support</li>
                <li>✔ Auto-save history</li>
                <li>✔ Dummy text generator</li>
              </ul>
            </div>
            <div className="col-md-4 mb-3" id="contact">
              <h5>Contact</h5>
              <ul className="list-unstyled">
                <li>Email: pavicontact14@gmail.com</li>
                <li>GitHub: github.com/dedupe-list</li>
              </ul>
            </div>
          </div>

          <hr className="border-light" />
          <p className="text-center mb-0 small">© 2025 DeDupeList+. Built with ❤️ using React & Bootstrap by Pavithran M.</p>
        </div>
      </footer>
    </div>
  );
}
